import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Game } from '../types';
import './GameDetail.css';
import SEO from '../components/SEO';
import { allGames, categories } from '../data/staticData';

const getGameUrl = (url: string) => {
  // 如果是本地开发环境，保持原样
  if (process.env.NODE_ENV === 'development') {
    return url;
  }
  // 如果是生产环境，转换为 HTTPS
  return url.replace('http://', 'https://');
};

// 工具函数：将字符串中的 \n 转为 <br />
function renderWithBreaks(text: string) {
  if (!text) return null;
  return text.split('\n').map((line, idx, arr) => (
    <React.Fragment key={idx}>
      {line}
      {idx < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

const GameDetail: React.FC = () => {
  const { urlkey } = useParams<{ urlkey: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showPlayNow, setShowPlayNow] = useState(false);
  const [mobileGameLoading, setMobileGameLoading] = useState(false);
  const [mobileLoadingProgress, setMobileLoadingProgress] = useState(0);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(80);
  const playNowId = useRef(1);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(window.navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchGame = async () => {
      try {
        // const response = await fetch(`/api/games/${urlkey}`);
        // if (!response.ok) {
        //   throw new Error(`HTTP error! status: ${response.status}`);
        // }
        // const data = await response.json();
        const data = allGames.filter(x => x.urlkey == urlkey)[0];

        var categoryName = categories.filter(x => x.id == data.category_id)[0].name;
        setGame({
          ...data,
          layout: data.layout || 'auto',
          categoryId: data.category_id,
          categoryName: categoryName
        });
        setError(null);
      } catch (error) {
        console.error('Error fetching game:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch game');
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [urlkey]);

  useEffect(() => {
    if (!loading && !isPlaying) {
      let startTime = Date.now();
      const duration = 2000; // 7 seconds
      
      const updateProgress = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);
        
        setLoadingProgress(progress);
        
        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          setShowPlayNow(true);
        }
      };
      
      requestAnimationFrame(updateProgress);
      
      return () => {
        setLoadingProgress(0);
        setShowPlayNow(false);
      };
    }
  }, [loading, isPlaying]);

  useEffect(() => {
    if (!loading && !isPlaying) {
      setShowPlayNow(true);
    }
  }, [loading, isPlaying]);

  useEffect(() => {
    if (isMobile && isPlaying) {
      setMobileGameLoading(true);
      setMobileLoadingProgress(0);
      
      let startTime = Date.now();
      const duration = 2000; // 7 seconds
      
      const updateProgress = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);
        
        setMobileLoadingProgress(progress);
        
        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          setMobileGameLoading(false);
        }
      };
      
      requestAnimationFrame(updateProgress);
    }
  }, [isMobile, isPlaying]);

  useEffect(() => {
    if (!loading && game) {
      // 记录最近玩过的游戏
      const key = 'recentGames';
      const stored = localStorage.getItem(key);
      let recent: any[] = stored ? JSON.parse(stored) : [];
      // 移除已存在的同id
      recent = recent.filter((g) => g.id !== game.id);
      // 添加到最前面
      recent.unshift({
        id: game.id,
        title: game.title,
        thumbnailUrl: game.thumbnailUrl,
        urlkey: game.urlkey,
      });
      // 最多10个
      if (recent.length > 10) recent = recent.slice(0, 10);
      localStorage.setItem(key, JSON.stringify(recent));
    }
  }, [loading, game]);

  const handlePlayNow = () => {
    if (game) {
      window.location.hash = `#play-now-${playNowId.current}`;
      playNowId.current += 1;
      setIsPlaying(true);
    }
  };

  const handleBackToDetails = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.log('Exit fullscreen failed:', error);
      }
    }
    setIsPlaying(false);
  };

  // Add touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY - currentY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    // 只有在拖动时才更新 currentY
    const newY = e.touches[0].clientY - startY;
    const maxY = window.innerHeight - 24; // Leave some space at bottom
    const minY = 24; // Leave some space at top
    const clampedY = Math.min(Math.max(newY, minY), maxY);
    setCurrentY(clampedY);
    if (backButtonRef.current) {
      backButtonRef.current.style.top = `${clampedY}px`;
      backButtonRef.current.style.transform = 'translateY(-50%)';
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Reset button position on orientation change or resize
  useEffect(() => {
    const handleResize = () => {
      setCurrentY(80);
      if (backButtonRef.current) {
        backButtonRef.current.style.top = '80px';
        backButtonRef.current.style.transform = 'translateY(-50%)';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleOrientationChange = async () => {
      if (isPlaying && !document.fullscreenElement) {
        try {
          await document.documentElement.requestFullscreen({
            navigationUI: 'hide'
          });
        } catch (error) {
          console.log('Fullscreen request failed:', error);
        }
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [isPlaying]);

  // 移动端全屏和方向控制
  useEffect(() => {
    if (!isMobile) return;
    // 进入游戏时
    if (isPlaying && game) {
      // 自动全屏
      const el = document.documentElement;
      if (el.requestFullscreen) {
        el.requestFullscreen().then(() => {
          // 屏幕方向控制
          const orientation = (window.screen.orientation || (window.screen as any).msOrientation || (window.screen as any).mozOrientation) as any;
          
          if (orientation && orientation.lock) {
            if (game.layout === 'landscape') {
              orientation.lock('landscape').catch(() => {});
            } else if (game.layout === 'portrait' || game.layout === 'auto' || !game.layout) {
              orientation.lock('portrait').catch(() => {});
            }
          }
        }).catch(error => {
          console.log('Fullscreen request failed:', error);
        });
      }
    }
    // 退出游戏时
    if (!isPlaying) {
      // 退出全屏
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      // 恢复竖屏
      const orientation = (window.screen.orientation || (window.screen as any).msOrientation || (window.screen as any).mozOrientation) as any;
      if (orientation && orientation.lock) {
        orientation.lock('portrait').catch(() => {});
      }
    }
  }, [isMobile, isPlaying, game]);

  if (loading) return <div className="loading">Loading game...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!game) return <div className="error">Game not found</div>;

    return (
    <>
      <SEO 
        title={`${game.title} - Play Online For Free On RandPlays`}
        description={game.seoDesc || game.description || ''}
        image={game.thumbnailUrl}
        url={`/game/${game.urlkey}`}
        type="game"
        keywords={`${game.title}, online game, browser game, free game`}
        rating={game.rating}
        playCount={game.ratingCount}
      />
      {/* ld+json 结构化数据 */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoGame",
          "name": game.title,
          "description": game.seoDesc || game.description || '',
          "image": game.thumbnailUrl,
          "url": window.location.origin + `/game/${game.urlkey}`,
          "applicationCategory": game.categoryName || undefined,
          "aggregateRating": game.rating ? {
            "@type": "AggregateRating",
            "ratingValue": game.rating,
            "ratingCount": game.ratingCount || 2391
          } : undefined,
          "playMode": "SinglePlayer",
          "operatingSystem": "Browser",
          "author": {
            "@type": "Organization",
            "name": "RandPlays"
          },
          "offers": {
            "@type": "Offer",
            "price": 0,
            "priceCurrency": "USD"
          },
          "breadcrumb": {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": window.location.origin + "/"
              },
              game.categoryId && game.categoryName ? {
                "@type": "ListItem",
                "position": 2,
                "name": game.categoryName,
                "item": window.location.origin + `/category/${game.categoryId}`
              } : null,
              {
                "@type": "ListItem",
                "position": 3,
                "name": game.title,
                "item": window.location.origin + `/game/${game.urlkey}`
              }
            ].filter(Boolean)
          }
        })
      }} />

      <div className="game-detail">
        {/* <div className="random-game-container">
          <button className="random-game-btn" onClick={() => window.location.href = '/random'}>
            <span className="dice-icon">🎲</span>
            Random Game
          </button>
        </div> */}
        {isMobile ? (
          <>
            {/* 详情内容 */}
            <div style={{ display: isPlaying ? 'none' : 'block' }}>
              <div className="game-preview">
                <div className="detail-thumbnail-container">
                  <img src={game.thumbnailUrl} alt={game.title} className="detail-game-thumbnail" />
                  <button className="play-now-btn" onClick={handlePlayNow}>
                    Play Now
                  </button>
                </div>
              </div>
              <div className="game-description">
                {/* 面包屑导航 */}
                <nav className="breadcrumb">
                  <a href="/">RandPlays</a>
                  <span> › </span>
                  {(() => {
                    const categoryId = game.categoryId;
                    const categoryName = game.categoryName;
                    return categoryId ? (
                      <Link to={`/category/${categoryId}`}>{categoryName}</Link>
                    ) : (
                      <span>{categoryName}</span>
                    );
                  })()}
                  <span> › </span>
                  <span>{game.title}</span>
                </nav>

                {/* 标题（白色板块专用 class） */}
                <h1 className="game-title-whiteboard">{game.title}</h1>

                {/* 副标题/作者（可选） */}
                {/* <div className="game-subtitle">{game.developer || 'LION STUDIOS'}</div> */}

                {/* 评分信息 */}
                <div className="game-rating-info">
                  <span className="rating-badge">
                    <span className="star">★</span>
                    {game.rating?.toFixed(1) ?? '--'}
                  </span>
                  <span className="rating-count">{game.ratingCount ?? 0} 次投票</span>
                </div>

                {/* 描述 */}
                <div className="desc-text">
                  <p>{renderWithBreaks(game.description || '')}</p>
                </div>

                {/* How To Play */}
                {game.howtoplay && (
                  <div className="game-howtoplay">
                    <h2>How To Play?</h2>
                    <p>{renderWithBreaks(game.howtoplay)}</p>
                  </div>
                )}
                {/* FAQ 区块，和howtoplay同级同样式 */}
                <div className="game-howtoplay">
                  <h2>How can I play {game.title} for free?</h2>
                  <p>You can play {game.title} for free on RandPlays.</p>
                </div>
                <div className="game-howtoplay">
                  <h2>Can I play {game.title} on mobile devices and desktop?</h2>
                  <p>{game.title} can be played on your computer and mobile devices like phones and tablets.</p>
                </div>
              </div>
            </div>
            {/* 游戏 iframe 始终挂载，只是隐藏/显示 */}
            <div className="mobile-game-view" style={{ display: isPlaying ? 'block' : 'none' }}>
              <button 
                ref={backButtonRef}
                className="back-to-details" 
                onClick={handleBackToDetails}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ top: `${currentY}px` }}
              >
                <div className="back-to-details-icon">
                  <svg viewBox="6 0 14 24" className="back-arrow-icon">
                    <path d="M9 19.91a2 2 0 0 1-1.403-3.424L12.15 12 7.597 7.514a2 2 0 1 1 2.806-2.85l6 5.91a2 2 0 0 1 0 2.85l-6 5.911A1.993 1.993 0 0 1 9 19.911z"></path>
                  </svg>
                  <img className="fav-icon" src="/favicon.ico" alt="favicon" />
                </div>
                {/* <div className="back-to-details-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                  </svg>
                </div> */}
              </button>
              <iframe 
                src={getGameUrl(game.gameUrl || '')} 
                title={game.title}
                className="mobile-game-frame"
                allow="fullscreen; autoplay; camera; focus-without-user-activation *; monetization; gamepad; keyboard-map *; xr-spatial-tracking; clipboard-write; web-share; accelerometer; magnetometer; gyroscope; display-capture"
                sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-same-origin allow-downloads allow-top-navigation"
                referrerPolicy="origin"
                loading="lazy"
              />
              {mobileGameLoading && (
                <div className="mobile-game-overlay">
                  <div className="loading-content">
                    <div className="loading-text">Loading Game...</div>
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar"
                        style={{ width: `${mobileLoadingProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {!isMobile ? (
              <div className={`desktop-game-container ${
                  urlkey === "Balatro-Online" ? "wide" : ""
                }`}>
                <iframe 
                  src={getGameUrl(game.gameUrl || '')} 
                  title={game.title}
                  className="desktop-game-frame"
                  allow="fullscreen; autoplay; camera; focus-without-user-activation *; monetization; gamepad; keyboard-map *; xr-spatial-tracking; clipboard-write; web-share; accelerometer; magnetometer; gyroscope; display-capture"
                  sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-same-origin allow-downloads allow-top-navigation"
                  referrerPolicy="origin"
                  loading="lazy"
                />
                {!isPlaying && (
                  <div className="game-overlay">
                    <div className="loading-content">
                      {!showPlayNow ? (
                        <>
                          <div className="loading-text">Loading...</div>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar" 
                              style={{ width: `${loadingProgress}%` }}
                            />
                          </div>
                        </>
                      ) : (
                        <button 
                          className="play-button ready"
                          onClick={handlePlayNow}
                        >
                          Play Now
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="game-preview">
                <div className="detail-thumbnail-container">
                  <img src={game.thumbnailUrl} alt={game.title} className="detail-game-thumbnail" />
                  <button className="play-now-btn" onClick={handlePlayNow}>
                    Play Now
                  </button>
                </div>
              </div>
            )}
            <div className={`game-description ${
                  urlkey === "Balatro-Online" ? "wide" : ""
                }`}>
              {/* 面包屑导航 */}
              <nav className="breadcrumb">
                <a href="/">RandPlays</a>
                <span> › </span>
                {(() => {
                  const categoryId = game.categoryId;
                  const categoryName = game.categoryName;
                  return categoryId ? (
                    <Link to={`/category/${categoryId}`}>{categoryName}</Link>
                  ) : (
                    <span>{categoryName}</span>
                  );
                })()}
                <span> › </span>
                <span>{game.title}</span>
              </nav>

              {/* 标题（白色板块专用 class） */}
              <h1 className="game-title-whiteboard">{game.title}</h1>

              {/* 副标题/作者（可选） */}
              {/* <div className="game-subtitle">{game.developer || 'LION STUDIOS'}</div> */}

              {/* 评分信息 */}
              <div className="game-rating-info">
                <span className="rating-badge">
                  <span className="star">★</span>
                  {game.rating?.toFixed(1) ?? '--'}
                </span>
                <span className="rating-count">{game.ratingCount ?? 0} 次投票</span>
              </div>

              {/* 描述 */}
              <div className="desc-text">
                <p>{renderWithBreaks(game.description || '')}</p>
              </div>

              {/* How To Play */}
              {game.howtoplay && (
                <div className="game-howtoplay">
                  <h2>How To Play?</h2>
                  <p>{renderWithBreaks(game.howtoplay)}</p>
                </div>
              )}
              {/* FAQ 区块，和howtoplay同级同样式 */}
              <div className="game-howtoplay">
                <h2>How can I play {game.title} for free?</h2>
                <p>You can play {game.title} for free on RandPlays.</p>
             </div>
              <div className="game-howtoplay">
                <h2>Can I play {game.title} on mobile devices and desktop?</h2>
                <p>{game.title} can be played on your computer and mobile devices like phones and tablets.</p>
              </div>
            </div>
          </>
        )}
        
      </div>
    </>
  );
};

export default GameDetail; 